import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
// Auth handled via MST AuthStore
import { useAuthStore } from "@/hooks/useAuthStore";
import { sanitizeString } from "@/utils/sanitization";
import { logger } from "@/utils/logger";
import { cn } from "@/lib/utils";
import { OrganizationSelect } from "@/components/auth/OrganizationSelect";
import { FRANKLIN_SIGNUP_URL } from "@/components/auth/LoginBrandPanel";
import {
  AUTH_FIELD_CLASSES,
  AUTH_LINK_CLASSES,
  AUTH_PRIMARY_ACTION_CLASSES,
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";
import { useAuth } from "@/hooks/useAuth";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { ResendVerificationLink } from "@/components/auth/ResendVerificationLink";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { isUnconfirmedUser } from "@/utils/authUtils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";

export default function Login() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const auth = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState("");

  const formSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, { message: t.forms.validation.required })
          .email({ message: t.forms.error.invalidEmail }),
        password: z.string().min(1, { message: t.forms.validation.required }),
      }),
    [t]
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const returnUrl = location.state?.returnUrl || "/buildings";

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && !hasRedirected) {
      setHasRedirected(true);
      logger.debug(
        "auth",
        "already-authenticated",
        "User already authenticated, redirecting to return URL"
      );
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, returnUrl, hasRedirected]);

  // Security enhancement: Rate limiting for login attempts
  useEffect(() => {
    if (loginAttempts > 5) {
      const timeout = setTimeout(() => {
        setLoginAttempts(0);
      }, 60000); // Reset after 1 minute

      return () => clearTimeout(timeout);
    }
  }, [loginAttempts]);

  const handleSubmit = form.handleSubmit(async (values) => {
    // Security enhancement: Rate limiting
    if (loginAttempts > 5) {
      toast.error(t.forms.error.tooManyAttempts);
      logger.warn("auth", "login-rate-limited", "User exceeded login attempts", {
        email: values.email.substring(0, 3) + "***",
      });
      return;
    }

    setLoading(true);

    try {
      // Log login attempt (partial email for privacy)
      logger.info("auth", "login-attempt", "User attempting to log in", {
        emailDomain: values.email.split("@")[1],
        hasPassword: !!values.password,
      });

      await auth.loginWithEmailPassword(sanitizeString(values.email.trim()), values.password);

      if (auth.error) {
        logger.warn("auth", "login-failed", "Login attempt failed", {
          error: auth.error,
          emailDomain: values.email.split("@")[1],
        });
        setLoginAttempts((prev) => prev + 1);

        // Detect "Email not confirmed" and surface in-place resend UI instead of a toast.
        // isUnconfirmedUser deliberately does NOT match "Invalid login credentials" to
        // prevent account enumeration (see src/utils/authUtils.ts).
        if (isUnconfirmedUser({ message: auth.error })) {
          setUnconfirmedEmail(values.email);
          setShowResendVerification(true);
          return;
        }

        toast.error(auth.error || t.forms.error.invalidCredentials);
      } else {
        logger.info("auth", "login-successful", "User logged in successfully", {
          userId: (auth.userId || "unknown").substring(0, 8) + "...",
        });
        toast.success(t.forms.success.loginSuccessful);

        setHasRedirected(true);
        // Force navigation after successful login
        navigate(returnUrl, { replace: true });
      }
    } catch (error) {
      logger.error("auth", "login-error", "Unexpected error during login", error as Error);
      setLoginAttempts((prev) => prev + 1);
      toast.error(t.forms.error.unexpectedError);
    } finally {
      setLoading(false);
    }
  });

  // If already redirected, don't show the login screen again
  if (!authLoading && isAuthenticated && hasRedirected) {
    return null;
  }

  return (
    <AuthPageLayout>
      <AuthPageHeader title={t.forms.login.heading} className="mb-8" />

      <Form {...form}>
        <form onSubmit={handleSubmit} noValidate>
          {showResendVerification && (
            <Alert className="mb-5 rounded-xl">
              <AlertDescription className="space-y-2">
                <p>{t.auth.emailVerificationRequired}</p>
                <ResendVerificationLink
                  email={unconfirmedEmail}
                  redirectUrl={
                    `${window.location.origin}/auth/confirm` +
                    `?next=${encodeURIComponent(returnUrl)}` +
                    `&email=${encodeURIComponent(unconfirmedEmail)}`
                  }
                  variant="outline"
                  size="sm"
                  className="w-full"
                />
              </AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="mb-[18px] space-y-2">
                <FormLabel htmlFor="email" className="text-[13px] font-semibold">
                  {t.forms.login.emailLabel}
                </FormLabel>
                <FormControl>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t.forms.login.emailPlaceholder}
                    className={AUTH_FIELD_CLASSES}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="mb-[18px] space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <FormLabel htmlFor="password" className="text-[13px] font-semibold">
                    {t.forms.login.passwordLabel}
                  </FormLabel>
                  <Link to="/forgot-password" className={cn(AUTH_LINK_CLASSES, "text-[12.5px]")}>
                    {t.forms.login.forgotPassword}
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder={t.forms.login.passwordPlaceholder}
                      className={cn(AUTH_FIELD_CLASSES, "pr-12")}
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg text-neutral-500 hover:text-foreground"
                      aria-label={showPassword ? t.forms.hidePassword : t.forms.showPassword}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Only reachable in the brief window before the redirect-when-authenticated effect fires. */}
          {isAuthenticated && (
            <div className="mb-[18px]">
              <OrganizationSelect />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || loginAttempts > 5}
            className={cn(AUTH_PRIMARY_ACTION_CLASSES, "mt-2")}
          >
            {loading ? (
              t.forms.status.saving
            ) : (
              <>
                {t.forms.login.submitButton}
                <ArrowRight className="h-[17px] w-[17px]" />
              </>
            )}
          </Button>
        </form>
      </Form>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-[12.5px] uppercase tracking-wide text-muted-foreground">
          {t.auth.orContinueWith}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <GoogleSignInButton returnUrl={returnUrl} className={AUTH_SECONDARY_ACTION_CLASSES} />

      <p className="mt-6 text-center text-[13.5px] text-muted-foreground">
        {t.forms.login.noAccountYet}{" "}
        <a
          href={FRANKLIN_SIGNUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={AUTH_LINK_CLASSES}
        >
          {t.forms.login.signUpLink}
        </a>
        .
      </p>

      {!import.meta.env.PROD && (
        <p className="mt-2 text-center text-[13.5px] text-muted-foreground">
          <Link to="/signup" className={AUTH_LINK_CLASSES}>
            {t.forms.login.signUpLink}
          </Link>
        </p>
      )}
    </AuthPageLayout>
  );
}
